import { useState } from 'react';
import { ApiError, newIdempotencyKey } from '../lib/api';
import { formatDate, formatNumber } from '../lib/format';
import { useI18n, type Language } from '../lib/i18n';
import { useMyRedemptions, useRedeemReward, useRewards, type RewardOffer } from '../lib/queries';
import { Button, Card, Skeleton } from '../components/ui';

const text = (value: Record<string, string> | null, language: Language): string =>
  value?.[language] ?? value?.['en'] ?? '';

export function RewardsPage() {
  const { t, language } = useI18n();
  const { data, isPending } = useRewards();
  const { data: mine } = useMyRedemptions();
  const redeem = useRedeemReward();
  /*
   * The key belongs to the decision, not to the tap.
   *
   * Minted when the confirmation opens, so a second press inside the same
   * dialog — or a retry over a bad connection — reaches the server as the
   * same redemption rather than buying the reward twice.
   */
  const [confirming, setConfirming] = useState<{ reward: RewardOffer; key: string } | null>(null);

  const error = redeem.error instanceof ApiError ? redeem.error : null;
  const live = (mine?.items ?? []).filter((r) => r.status === 'ISSUED');
  const spent = (mine?.items ?? []).filter((r) => r.status !== 'ISSUED');

  if (isPending) return <Skeleton className="h-64" />;
  if (data && !data.enabled) {
    return <Card className="py-10 text-center"><p className="text-sm text-ink-dim">{t('rewards.empty')}</p></Card>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-ink">{t('rewards.title')}</h1>
        <p className="tabular mt-1 text-sm text-brand">
          {formatNumber(data?.pointsBalance ?? 0, language)} · {t('rewards.balance')}
        </p>
      </div>

      {error && (
        <Card className="border-danger/35 bg-danger/10">
          <p className="text-sm text-ink">{error.message}</p>
        </Card>
      )}

      {live.length > 0 && (
        <section>
          <h2 className="mb-2 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
            {t('rewards.mine')}
          </h2>
          <div className="space-y-2">
            {live.map((entry) => (
              <Card key={entry.id} className="border-brand/35 bg-brand-wash/40">
                <div className="text-sm font-semibold text-ink">{text(entry.name, language)}</div>
                <div className="tabular mt-2 text-center text-xl font-bold tracking-[0.18em] text-brand">
                  {entry.code}
                </div>
                <div className="mt-2 text-center text-[0.68rem] text-ink-faint">
                  {t('rewards.showCode')}
                  {entry.expiresAt && (
                    <span className="mt-0.5 block">
                      {t('rewards.expires')} {formatDate(entry.expiresAt, language)}
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        {data?.items.length === 0 && (
          <Card className="py-8 text-center">
            <p className="text-sm text-ink-dim">{t('rewards.empty')}</p>
          </Card>
        )}

        {data?.items.map((reward) => {
          const blocked = !reward.available;
          const ready = reward.affordable && reward.available;
          return (
            <Card key={reward.id} className={blocked ? 'opacity-50' : ''}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink">{text(reward.name, language)}</div>
                  {reward.description && (
                    <p className="mt-1 text-xs leading-relaxed text-ink-dim">
                      {text(reward.description, language)}
                    </p>
                  )}
                </div>
                <div className="tabular shrink-0 text-right">
                  <div className={`text-base font-bold ${ready ? 'text-brand' : 'text-ink-dim'}`}>
                    {formatNumber(reward.pointsCost, language)}
                  </div>
                  <div className="text-[0.6rem] uppercase tracking-wider text-ink-faint">pts</div>
                </div>
              </div>

              <div className="mt-3">
                {blocked ? (
                  <p className="text-center text-xs text-ink-faint">{t('rewards.gone')}</p>
                ) : ready ? (
                  <Button className="w-full" onClick={() => setConfirming({ reward, key: newIdempotencyKey() })}>
                    {t('rewards.redeem')}
                  </Button>
                ) : (
                  <p className="text-center text-xs text-ink-faint">
                    {t('rewards.short')} {formatNumber(reward.pointsShort, language)}
                  </p>
                )}
              </div>
            </Card>
          );
        })}
      </section>

      {spent.length > 0 && (
        <section>
          <h2 className="mb-2 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
            {t('rewards.past')}
          </h2>
          <div className="space-y-1.5">
            {spent.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface px-3 py-2.5 opacity-60"
              >
                <span className="truncate text-xs text-ink-dim">{text(entry.name, language)}</span>
                <span className="tabular shrink-0 text-[0.65rem] text-ink-faint">
                  {entry.status === 'USED' ? t('rewards.used') : t('rewards.expired')}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {confirming && (
        <div className="fixed inset-0 z-40 grid place-items-end bg-black/70 backdrop-blur-[2px] sm:place-items-center">
          <div className="w-full rounded-t-3xl border-t border-line bg-surface p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:max-w-sm sm:rounded-3xl sm:border">
            <p className="text-center text-sm text-ink-dim">{t('rewards.confirm')}</p>
            <p className="mt-2 text-center font-display text-xl font-bold text-ink">
              {text(confirming.reward.name, language)}
            </p>
            <p className="tabular mt-1 text-center text-sm text-brand">
              −{formatNumber(confirming.reward.pointsCost, language)} pts
            </p>
            <div className="mt-5 space-y-2">
              <Button
                className="w-full"
                loading={redeem.isPending}
                onClick={() => redeem.mutate({ rewardId: confirming.reward.id, key: confirming.key }, { onSuccess: () => setConfirming(null) })}
              >
                {t('rewards.confirmYes')}
              </Button>
              <Button variant="quiet" className="w-full" onClick={() => setConfirming(null)}>
                {t('rewards.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
